// Shared EPA Tier II code tables (40 CFR §370.43).
// Single source of truth for the report form, export preview, and XML generator.
import type { HazKey } from '../../tri-shared/services/hazard-classification.ts';

export const TIER2_STORAGE_TYPES = [
  { code: 'A', label: 'Above ground tank' },
  { code: 'B', label: 'Below ground tank' },
  { code: 'C', label: 'Tank inside building' },
  { code: 'D', label: 'Steel drum' },
  { code: 'E', label: 'Plastic/non-metallic drum' },
  { code: 'F', label: 'Can' },
  { code: 'G', label: 'Carboy' },
  { code: 'H', label: 'Silo' },
  { code: 'I', label: 'Fiber drum' },
  { code: 'J', label: 'Bag' },
  { code: 'K', label: 'Box' },
  { code: 'L', label: 'Cylinder' },
  { code: 'M', label: 'Glass bottles/jugs' },
  { code: 'N', label: 'Plastic bottles/jugs' },
  { code: 'O', label: 'Tote bin/IBC' },
  { code: 'P', label: 'Tank wagon/trailer' },
  { code: 'Q', label: 'Rail car' },
  { code: 'R', label: 'Other' },
] as const;

export const TIER2_PRESSURES = [
  { code: 'A', label: 'Ambient pressure' },
  { code: 'B', label: 'Above ambient pressure' },
  { code: 'C', label: 'Below ambient pressure' },
  { code: 'D', label: 'Cryogenic liquid' },
  { code: 'E', label: 'Varies or unknown' },
] as const;

export const TIER2_TEMPERATURES = [
  { code: 'A', label: 'Ambient temperature' },
  { code: 'B', label: 'Above ambient temperature' },
  { code: 'C', label: 'Below ambient/refrigerated' },
  { code: 'D', label: 'Cryogenic' },
  { code: 'E', label: 'Varies or unknown' },
] as const;

/** Returns the two-digit EPA amount range code, or '' when no amount is known. */
export function rangeCode(lbs: number | null): string {
  if (lbs == null || !Number.isFinite(lbs) || lbs < 0) return '';
  if (lbs < 100) return '01';
  if (lbs < 500) return '02';
  if (lbs < 1_000) return '03';
  if (lbs < 5_000) return '04';
  if (lbs < 10_000) return '05';
  if (lbs < 25_000) return '06';
  if (lbs < 50_000) return '07';
  if (lbs < 75_000) return '08';
  if (lbs < 100_000) return '09';
  if (lbs < 500_000) return '10';
  if (lbs < 1_000_000) return '11';
  if (lbs < 10_000_000) return '12';
  return '13';
}

export const RANGE_LABELS: Record<string, string> = {
  '01': '01  (0 – 99 lbs)',
  '02': '02  (100 – 499 lbs)',
  '03': '03  (500 – 999 lbs)',
  '04': '04  (1,000 – 4,999 lbs)',
  '05': '05  (5,000 – 9,999 lbs)',
  '06': '06  (10,000 – 24,999 lbs)',
  '07': '07  (25,000 – 49,999 lbs)',
  '08': '08  (50,000 – 74,999 lbs)',
  '09': '09  (75,000 – 99,999 lbs)',
  '10': '10  (100,000 – 499,999 lbs)',
  '11': '11  (500,000 – 999,999 lbs)',
  '12': '12  (1,000,000 – 9,999,999 lbs)',
  '13': '13  (10,000,000+ lbs)',
};

/**
 * GHS hazard fields mapped to the five legacy Tier II categories per the
 * NOAA/EPA data standard (Fire | Sudden Release of Pressure | Reactive | Acute | Chronic).
 * Single source of truth for both the export preview display and the XML generator,
 * so the on-screen categories can never drift from what is actually exported.
 */
export const LEGACY_HAZARD_CATEGORIES: { label: string; icon: string; keys: HazKey[] }[] = [
  { label: 'Fire', icon: 'fa-fire', keys: ['hazFlammable', 'hazCombustibleDust', 'hazPyrophoric'] },
  { label: 'Sudden Release of Pressure', icon: 'fa-gauge-high', keys: ['hazGasUnderPressure'] },
  {
    label: 'Reactive',
    icon: 'fa-atom',
    keys: [
      'hazExplosive',
      'hazSelfReactive',
      'hazOrganicPeroxide',
      'hazOxidizer',
      'hazSelfHeating',
      'hazCorrosiveToMetal',
      'hazOtherPhysical',
    ],
  },
  {
    label: 'Acute',
    icon: 'fa-skull-crossbones',
    keys: [
      'hazAcuteToxicity',
      'hazSkinCorrosion',
      'hazSeriousEyeDamage',
      'hazStotSingleExposure',
      'hazAspirationHazard',
      'hazSimpleAsphyxiant',
    ],
  },
  {
    label: 'Chronic',
    icon: 'fa-heart-pulse',
    keys: [
      'hazRespiratorySensitizer',
      'hazGermCellMutagenicity',
      'hazCarcinogenicity',
      'hazReproductiveToxicity',
      'hazStotRepeatedExposure',
      'hazHnoc',
    ],
  },
];

/**
 * Current Tier2 Submit hazard category spellings. The national DTD leaves the
 * category as text, but Tier2 Submit imports use these exact GHS/HazCom labels.
 * Keep the legacy five-category rollup above for the human summary only.
 */
export const TIER2_SUBMIT_HAZARD_CATEGORIES: {
  label: string;
  keys: HazKey[];
}[] = [
  { label: 'Explosive', keys: ['hazExplosive'] },
  { label: 'Flammable (gases, aerosols, liquids, or solids)', keys: ['hazFlammable'] },
  { label: 'Oxidizer (liquid, solid or gas)', keys: ['hazOxidizer'] },
  { label: 'Self-reactive', keys: ['hazSelfReactive'] },
  { label: 'Pyrophoric (liquid or solid)', keys: ['hazPyrophoric'] },
  { label: 'Self-heating', keys: ['hazSelfHeating'] },
  { label: 'Organic peroxide', keys: ['hazOrganicPeroxide'] },
  { label: 'Corrosive to metal', keys: ['hazCorrosiveToMetal'] },
  { label: 'Gas under pressure (compressed gas)', keys: ['hazGasUnderPressure'] },
  { label: 'In contact with water emits flammable gas', keys: ['hazOtherPhysical'] },
  { label: 'Combustible Dust', keys: ['hazCombustibleDust'] },
  { label: 'Acute toxicity (any route of exposure)', keys: ['hazAcuteToxicity'] },
  { label: 'Skin corrosion or irritation', keys: ['hazSkinCorrosion'] },
  { label: 'Serious eye damage or eye irritation', keys: ['hazSeriousEyeDamage'] },
  { label: 'Respiratory or skin sensitization', keys: ['hazRespiratorySensitizer'] },
  { label: 'Germ cell mutagenicity', keys: ['hazGermCellMutagenicity'] },
  { label: 'Carcinogenicity', keys: ['hazCarcinogenicity'] },
  { label: 'Reproductive toxicity', keys: ['hazReproductiveToxicity'] },
  {
    label: 'Specific target organ toxicity(single or repeated exposure)',
    keys: ['hazStotSingleExposure', 'hazStotRepeatedExposure'],
  },
  { label: 'Aspiration hazard', keys: ['hazAspirationHazard'] },
  { label: 'Simple Asphyxiant', keys: ['hazSimpleAsphyxiant'] },
  { label: 'Hazard Not Otherwise Classified', keys: ['hazHnoc'] },
];

/** Old frozen reports predate the editable hazard. Never override an explicit false. */
export function reportHazardActive(
  entry: Partial<Record<HazKey, boolean>> & { isCarcinogen?: boolean },
  key: HazKey,
): boolean {
  return (key === 'hazCarcinogenicity' ? (entry.hazCarcinogenicity ?? entry.isCarcinogen) : entry[key]) === true;
}
